/* Quote List, Vue Component (BCR-156 Stage 2 - backed by the Quote entity) */
<template>
  <v-card id="quote-list">
    <v-card-title>Quotes</v-card-title>

    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <!-- status filter (spec 6.1.1 - radio selectors, default Pending) -->
      <v-radio-group v-model="statusFilter" row hide-details class="mt-0 pt-0 mb-4" @change="searchData">
        <v-radio label="Pending" value="Pending"></v-radio>
        <v-radio label="Completed" value="Completed"></v-radio>
        <v-radio label="Rejected" value="Rejected"></v-radio>
        <v-radio label="Cancelled" value="Cancelled"></v-radio>
        <v-radio label="All" value="All"></v-radio>
      </v-radio-group>

      <v-spacer></v-spacer>

      <div class="d-flex align-center pb-5 flex-wrap">

        <v-text-field v-model="search.all" single-line dense outlined hide-details placeholder="Search"
          class="mb-4 mr-4" @keyup.enter="searchData"></v-text-field>

        <v-text-field v-model="search.Reference" single-line dense outlined hide-details placeholder="Quote Reference"
          class="mb-4 mr-4" @keyup.enter="searchData"></v-text-field>

        <v-text-field v-model="search.MemberId_FullName" single-line dense outlined hide-details
          placeholder="Customer Name" class="mb-4 mr-4" @keyup.enter="searchData"></v-text-field>

        <v-btn color="primary" class="mb-4 mr-4" @click="searchData">
          Search
        </v-btn>

      </div>
    </v-card-text>

    <!-- table -->
    <v-data-table :headers="tableColumns" :items="items" :options.sync="options"
      :server-items-length="totalItems" :loading="loading" :footer-props="footerProps" class="text-no-wrap">

      <template #[`item.Reference`]="{ item }">
        <router-link class="font-weight-medium text-decoration-none"
          :to="{ name: 'order-quote', params: { id: item.Id } }">
          {{ item.Reference }}
        </router-link>
      </template>

      <template #[`item.Dimensions`]="{ item }">
        <span v-if="item.Length || item.Width || item.Height">
          {{ item.Length }}x{{ item.Width }}x{{ item.Height }} mm
        </span>
      </template>

      <template #[`item.QuotedCost`]="{ item }">
        <!-- prefer the converted order's current invoice amount (base + extras, whatever the
             pricing method) over the quote's own QuotedCost, which is always base-only -
             see Quote.OrderId_InvoiceAmount -->
        <span v-if="item.Status == 'Completed' && item.OrderId_InvoiceAmount != null">{{ Display.Money(item.OrderId_InvoiceAmount) }}</span>
        <span v-else-if="item.Status == 'Completed' && item.QuotedCost != null">{{ Display.Money(item.QuotedCost) }}</span>
        <span v-else>&mdash;</span>
      </template>

      <template #[`item.Status`]="{ item }">
        <v-chip small :color="statusColor(item.Status)" outlined class="v-chip-light-bg">{{ item.Status }}</v-chip>
      </template>

      <template #[`item.Created`]="{ item }">
        {{ Display.DateTime(item.Created) }}
      </template>

      <!-- actions -->
      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center">
          <Can I="create" on="Quotes" v-if="item.Status == 'Pending'">
            <v-btn small color="success" :to="{ name: 'order-quote', params: { id: item.Id } }">Process</v-btn>
          </Can>
          <Can I="read" on="Quotes" v-else>
            <v-btn small color="secondary" :to="{ name: 'order-quote', params: { id: item.Id } }">View</v-btn>
          </Can>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>

import store from '@/store'
import { Can } from '@casl/vue'
import { ref, watch, computed } from '@vue/composition-api'
import * as Display from '@/datahelpers/displayTypes'

export default {
  components: {
    Can
  },
  setup() {

    const tableColumns = [
      {
        text: '',
        value: 'actions',
        align: 'center',
        sortable: false,
      },
      { text: 'Reference', value: 'Reference' },
      { text: 'Customer', value: 'MemberId_FullName' },
      { text: 'Member', value: 'MemberId_Reference' },
      { text: 'Description', value: 'ItemDescription' },
      { text: 'Dimensions', value: 'Dimensions', sortable: false },
      { text: 'Created', value: 'Created' },
      { text: 'Total', value: 'QuotedCost' },
      { text: 'Status', value: 'Status' },
    ];

    // default to Pending (spec 6.1.1 rule 02)
    const statusFilter = ref('Pending');

    const search = ref({});
    const filter = ref({ Statuses: ['Pending'] });
    const options = ref({
      sortBy: ['Created'],
      sortDesc: [true],
      itemsPerPage: 20
    });
    const footerProps = {
      'items-per-page-options': [10, 20, 50, 100, -1]
    };

    const items = computed(() => store.state.app.Quotes || []);
    const totalItems = computed(() => store.state.app.QuotesTotal || 0);
    const loading = ref(false);

    const statusColor = (status) => {
      if (status == 'Pending') return 'warning';
      if (status == 'Completed') return 'success';
      if (status == 'Rejected') return 'error';
      if (status == 'Cancelled') return 'secondary';
      return 'primary';
    };

    const fetchData = () => {
      // translate the radio selection into the status list filter
      filter.value.Statuses = statusFilter.value === 'All' ? [] : [statusFilter.value];

      store
        .dispatch('app/fetchQuotes', {
          search: search.value,
          options: options.value,
          filter: filter.value,
        })
        .then(() => {
          loading.value = false
        })
        .catch(error => {
          loading.value = false
          console.log(error)
        })
    }

    watch([options], () => {
      loading.value = true
      fetchData()
    }, { deep: true });

    function searchData() {
      this.$set(this.options, 'page', 1);
      loading.value = true
      fetchData()
    }

    return {
      searchData,
      tableColumns,
      search,
      statusFilter,
      filter,
      options,
      totalItems,
      items,
      loading,
      footerProps,
      statusColor,
      Display,
    }
  }
}
</script>

<style lang="scss" scoped></style>
